/**
 * Service for managing recent users for quick re-login
 */

export interface RecentUser {
  id: string;
  fullName: string;
  username?: string;
  email?: string;
  avatar?: string;
  roles?: string[];
  lastLoginAt: number;
}

const STORAGE_KEY = 'recent_users';
const MAX_RECENT_USERS = 5; // Maximum number of recent users to store

class RecentUsersService {
  /**
   * Get all recent users sorted by last login time (most recent first)
   */
  getRecentUsers(): RecentUser[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];

      const users: RecentUser[] = JSON.parse(stored);
      // Sort by last login time (most recent first)
      return users.sort((a, b) => b.lastLoginAt - a.lastLoginAt);
    } catch (error) {
      console.error('Failed to get recent users:', error);
      return [];
    }
  }

  /**
   * Add or update a user in recent users list
   */
  addRecentUser(user: {
    id: string;
    fullName?: string;
    name?: string;
    username?: string;
    email?: string;
    avatar?: string;
    roles?: string[];
  }): void {
    try {
      const recentUsers = this.getRecentUsers();

      // Remove user if already exists
      const filtered = recentUsers.filter((u) => u.id !== user.id);

      // Add new user at the beginning
      const newUser: RecentUser = {
        id: user.id,
        fullName: user.fullName || user.name || user.username || user.email || 'User',
        username: user.username,
        email: user.email,
        avatar: user.avatar,
        roles: user.roles,
        lastLoginAt: Date.now(),
      };

      const updated = [newUser, ...filtered].slice(0, MAX_RECENT_USERS);

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to add recent user:', error);
    }
  }

  /**
   * Remove a user from recent users list
   */
  removeRecentUser(userId: string): void {
    try {
      const recentUsers = this.getRecentUsers();
      const filtered = recentUsers.filter((u) => u.id !== userId);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    } catch (error) {
      console.error('Failed to remove recent user:', error);
    }
  }

  /**
   * Clear all recent users
   */
  clearRecentUsers(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear recent users:', error);
    }
  }
}

export const recentUsersService = new RecentUsersService();
